<template>
  <div class="protocol-matrix-container">
    <div ref="matrixScrollEl" class="matrix-scroll card-surface">
      <!-- Sticky action/header bar: independent of horizontal table scroll -->
      <div class="matrix-topbar">
        <button
          class="btn-back protocol-back-btn"
          @click="$emit('edit-template')"
          title="Back"
        >
          Back
        </button>

        <h2 class="screen-title">Schedule of Assessments</h2>

        <div class="matrix-topbar-actions">
          <button class="icon-btn header-info-btn" @click="showInfo = true" aria-label="What is Protocol Matrix?">
            <i :class="icons.info"></i>
          </button>

          <button @click="$emit('edit-template')" class="btn-option">Back to Edit Template</button>
          <button @click="openPreview" :disabled="!hasAnyAssignment" class="btn-option">Preview</button>

          <!-- FIX: explicit publish mode -->
          <button @click="saveStudy('publish')" class="btn-primary" :disabled="isPublishingStudy">
            Publish Study
          </button>
        </div>
      </div>

      <!-- 2. Protocol Matrix -->
      <table class="protocol-table">
        <thead>
          <tr>
            <th rowspan="2" class="model-header-col">
              <div class="model-header-main">
                <span class="model-header-title">Template</span>

                <label
                  v-if="visitList.length && groupList.length && selectedModels.length"
                  class="bulk-toggle small model-global-toggle"
                >
                  <input
                    type="checkbox"
                    :checked="isEverythingSelected"
                    @change="onToggleAll($event.target.checked)"
                  />
                  <span>Assign all sections to all visits</span>
                </label>
              </div>
            </th>

            <th
              v-for="(visit, vIdx) in visitList"
              :key="`vh-${vIdx}`"
              :colspan="groupList.length"
              class="visit-header-col"
            >
              <div class="visit-header-cell">
                <div class="visit-header-main">
                  <span class="th-title">Visit:</span>
                  <span class="th-chip">{{ visit.name }}</span>
                </div>
                <label
                  v-if="groupList.length && selectedModels.length"
                  class="bulk-toggle small"
                >
                  <input
                    type="checkbox"
                    :checked="isVisitFullySelected(vIdx)"
                    @change="onToggleVisitAll(vIdx, $event.target.checked)"
                  />
                  <span>All forms</span>
                </label>
              </div>
            </th>
          </tr>

          <tr>
            <template v-for="(_, vIdx) in visitList" :key="`vg-${vIdx}`">
              <th
                v-for="(group, gIdx) in groupList"
                :key="`vg-${vIdx}-${gIdx}`"
                class="group-header-col"
              >
                <span class="group-name">Group: {{ group.name }}</span>
              </th>
            </template>
          </tr>
        </thead>

        <tbody>
          <!-- hide models with no fields -->
          <tr
            v-for="(model, mIdx) in selectedModels"
            :key="`m-${mIdx}`"
            v-show="model.fields && model.fields.length"
          >
            <td class="model-cell">
              <div class="model-header-row">
                <span class="model-title">{{ model.title }}</span>
                <label
                  v-if="visitList.length && groupList.length"
                  class="bulk-toggle small"
                >
                  <input
                    type="checkbox"
                    :checked="isModelFullySelected(mIdx)"
                    @change="onToggleModelAll(mIdx, $event.target.checked)"
                  />
                  <span>All visits</span>
                </label>
              </div>
            </td>

            <!-- Full-matrix cells -->
            <template v-for="(_, vIdx) in visitList" :key="`row-${mIdx}-v-${vIdx}`">
              <td
                v-for="(_, gIdx) in groupList"
                :key="`cell-${mIdx}-${vIdx}-${gIdx}`"
                class="assignment-cell"
              >
                <label class="chk-wrap" :title="`Toggle ${model.title} @ ${visitList[vIdx]?.name} / ${groupList[gIdx]?.name}`">
                  <input
                    type="checkbox"
                    :checked="assignments[mIdx][vIdx][gIdx]"
                    @change="onToggle(mIdx, vIdx, gIdx, $event.target.checked)"
                  />
                  <!-- Unchecked: outline square; Checked: check-square -->
                  <i class="fa-chk" :class="[assignments[mIdx][vIdx][gIdx] ? 'fas fa-check-square' : 'far fa-square']"></i>
                </label>
              </td>
            </template>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="isPublishingStudy"
      class="publishing-overlay"
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="publishing-panel">
        <div class="publishing-spinner" aria-hidden="true"></div>
        <div class="publishing-title">Please wait</div>
        <div class="publishing-text">Study is being published.</div>
      </div>
    </div>

    <!-- 3. Preview Modal -->
    <div v-if="showPreviewModal" class="modal-overlay">
      <div class="modal protocol-preview-modal">
        <div class="preview-header">
          <!-- Visit nav -->
          <button @click="prevPreviewVisit" :disabled="previewVisitIndex === 0" class="nav-btn">&lt;</button>
          <span class="preview-label">
            Visit:
            <span class="th-chip">{{ visitList[previewVisitIndex].name }}</span>
          </span>
          <button @click="nextPreviewVisit" :disabled="previewVisitIndex === visitList.length - 1" class="nav-btn">&gt;</button>
          <span class="spacer"></span>
          <!-- Group nav -->
          <button @click="prevPreviewGroup" :disabled="previewGroupPos === 0" class="nav-btn">&lt;</button>
          <span class="preview-label">
            Group:
            <span class="th-chip">{{ groupList[assignedGroups[previewGroupPos]]?.name }}</span>
          </span>
          <button
            @click="nextPreviewGroup"
            :disabled="previewGroupPos === assignedGroups.length - 1"
            class="nav-btn"
          >&gt;</button>
        </div>

        <div class="preview-content">
          <FormPreview :form="previewForm" />
        </div>
        <div class="modal-actions">
          <button @click="closePreview" class="btn-option">
            <i :class="icons.times" class="mr-6"></i> Close
          </button>
        </div>
      </div>
    </div>

    <!-- 4. Custom Dialog for Notifications (errors/general) -->
    <div v-if="showDialog && !showLogicalIssuesDialog" class="modal-overlay">
      <div class="modal validation-modal pretty-error-modal">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> Please fix the following
        </h3>

        <div class="pretty-error-content">
          <template v-if="dialogListItems.length">
            <ol class="pretty-error-list">
              <li
                v-for="(item, idx) in dialogListItems"
                :key="`dialog-item-${idx}`"
                class="pretty-error-item"
              >
                {{ item }}
              </li>
            </ol>
          </template>

          <template v-else>
            <p class="validation-text">{{ dialogMessage }}</p>
          </template>
        </div>

        <div class="modal-actions">
          <button class="btn-primary" @click="closeDialog">
            <i :class="icons.check" class="mr-6"></i> OK
          </button>
        </div>
      </div>
    </div>

    <!-- 4a. Logical inconsistencies dialog -->
    <div v-if="showLogicalIssuesDialog" class="modal-overlay">
      <div class="modal validation-modal logical-issues-modal">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> Check visit/group assignments
        </h3>

        <p class="validation-text">
          Some field dependencies are incomplete in the selected visit/group combinations.
          Please assign the dependent sections together before saving.
        </p>

        <div class="logical-issues-scroll">
          <div
            v-for="(group, idx) in logicalIssuesGrouped"
            :key="`logic-group-${idx}`"
            class="logic-group-card"
          >
            <div class="logic-group-header">
              <span class="logic-group-chip">
                Visit: {{ group.visitName }}
              </span>
              <span class="logic-group-chip">
                Group: {{ group.groupName }}
              </span>
            </div>

            <div class="logic-dependency-list">
              <div
                v-for="(issue, issueIdx) in group.issues"
                :key="`logic-issue-${idx}-${issueIdx}`"
                class="logic-dependency-item"
              >
                <div class="logic-arrow-row">
                  <div class="logic-node logic-node-current">
                    <div class="logic-node-section">{{ issue.targetSection }}</div>
                    <div class="logic-node-field">{{ issue.targetField }}</div>
                  </div>

                  <div class="logic-arrow-wrap">
                    <span class="logic-arrow-text">depends on</span>
                    <span class="logic-arrow-line">→</span>
                  </div>

                  <div class="logic-node logic-node-source">
                    <div class="logic-node-section">{{ issue.sourceSection }}</div>
                    <div class="logic-node-field">{{ issue.sourceField }}</div>
                  </div>
                </div>

                <div class="logic-dependency-note">
                  These two sections are not assigned together in this visit/group.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-actions">
          <button class="btn-primary" @click="closeLogicalIssuesDialog">
            <i :class="icons.check" class="mr-6"></i> OK
          </button>
        </div>
      </div>
    </div>

    <!-- 4a. Success dialog with View Saved Study CTA -->
    <div v-if="showSaveSuccessDialog" class="modal-overlay">
      <div class="modal validation-modal">
        <h3 class="validation-title">
          <i :class="icons.check" class="li-icon"></i> Save successful
        </h3>
        <p class="validation-text">{{ saveSuccessMessage }}</p>
        <div class="modal-actions">
          <button class="btn-option" @click="closeSaveSuccessDialog">
            <i :class="icons.times" class="mr-6"></i> Close
          </button>
          <button class="btn-primary" @click="onSuccessViewSavedStudy">
            <i :class="icons.check" class="mr-6"></i> View Saved Study
          </button>
        </div>
      </div>
    </div>

    <!-- 4b. Info dialog for ProtocolMatrix -->
    <div v-if="showInfo" class="modal-overlay">
      <div class="modal validation-modal">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> What is this screen?
        </h3>
        <p class="validation-text">
          This matrix helps you decide which <strong>forms/data models</strong> are collected for each
          study <strong>Visit</strong> and <strong>Group</strong>. Click a checkbox to assign a form
          to that visit and group. Use the arrows to move between visits when there are many.
        </p>
        <ul class="empty-visits-list">
          <li>Checked = that form will appear for participants in that group at that visit.</li>
          <li>Use <strong>Preview</strong> to see what participants will fill out.</li>
          <li><strong>Save</strong> stores your setup. We’ll warn you if a visit has nothing assigned.</li>
        </ul>
        <div class="modal-actions">
          <button class="btn-primary" @click="showInfo = false">
            <i :class="icons.check" class="mr-6"></i> Got it
          </button>
        </div>
      </div>
    </div>

    <!-- 4b. Empty-visit warning modal -->
    <div v-if="showEmptyVisitsModal" class="modal-overlay">
      <div class="modal validation-modal">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> Some visits have no assigned models
        </h3>
        <p class="validation-text">
          We found visits without any model assigned. You can go fix them, or save anyway.
        </p>
        <ul class="empty-visits-list">
          <li v-for="vIdx in emptyVisitIndices" :key="`empty-v-${vIdx}`">
            <span class="visit-item-title">Visit {{ vIdx + 1 }} — {{ visitList[vIdx]?.name || 'Visit' }}</span>
          </li>
        </ul>
        <div class="modal-actions">
          <button class="btn-option" @click="goToFirstEmptyVisit">
            <i :class="icons.exchangeAlt" class="mr-6"></i> Go to first empty visit
          </button>
          <button class="btn-primary" :disabled="isSavingInProgress" @click="saveAnyway">
            <i :class="icons.check" class="mr-6"></i> {{ isSavingInProgress ? 'Saving…' : 'Save anyway' }}
          </button>
          <button class="btn-option" @click="closeEmptyVisitsModal">
            <i :class="icons.times" class="mr-6"></i> Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- 4c. Confirm Template Changes (scrollable) -->
    <div v-if="showConfirmChanges" class="modal-overlay">
      <div class="modal validation-modal confirm-scroll">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> Confirm Template Changes
        </h3>
        <p class="validation-text">
          You made structural changes. Saving will create a <strong>new template version</strong>.
          Existing data remains attached to its original versions and is not lost.
        </p>

        <!-- Scrolling area -->
        <div class="diff-scroll">
          <TemplateDiffView v-if="snapshotFrom && snapshotTo" :from="snapshotFrom" :to="snapshotTo" />
        </div>

        <div class="modal-actions sticky-actions">
          <button class="btn-option" @click="cancelConfirm">
            <i :class="icons.times" class="mr-6"></i> Cancel
          </button>
          <button class="btn-primary" @click="confirmAndSave">
            <i :class="icons.check" class="mr-6"></i> Confirm &amp; Save
          </button>
        </div>
      </div>
    </div>

    <!-- Unsaved changes dialog -->
    <div v-if="showUnsavedDialog" class="modal-overlay">
      <div class="modal validation-modal">
        <h3 class="validation-title">
          <i :class="icons.infoCircle" class="li-icon"></i> Unsaved changes
        </h3>
        <p class="validation-text">
          You have unsaved changes in the Visit Schedule. You can keep editing, exit without saving,
          or save as draft and exit.
        </p>
        <div class="modal-actions unsaved-actions">
          <button class="btn-option" @click="onUnsavedKeepEditing" :disabled="unsavedBusy">
            Keep editing
          </button>
          <button class="btn-option" @click="onUnsavedExitWithoutSaving" :disabled="unsavedBusy">
            Exit without saving
          </button>
          <button class="btn-primary" @click="onUnsavedSaveAndExit" :disabled="unsavedBusy">
            {{ unsavedBusy ? "Saving…" : unsavedSaveLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, onBeforeUnmount, nextTick } from "vue";
import { useRouter, onBeforeRouteLeave } from "vue-router";
import { useStore } from "vuex";
import axios from "axios";
import FormPreview from "@/components/FormPreview.vue";
import TemplateDiffView, { deepClone, computeTemplateDiff } from "@/components/TemplateDiffView.vue";
import icons from "@/assets/styles/icons";
import { STUDY_BEFORE_IDLE_EVENT } from "@/utils/studySaveWorkflow";

export default {
  name: "ProtocolMatrix",
  components: { FormPreview, TemplateDiffView },
  props: {
    visits: { type: Array, required: true },
    groups: { type: Array, required: true },
    selectedModels: { type: Array, required: true },
    assignments: { type: Array, required: true },
    forms: { type: Array, required: false, default: () => [] }
  },
  emits: ["assignment-updated", "edit-template"],
  setup(props, { emit }) {
    const router = useRouter();
    const store = useStore();

    const isEditing = computed(() => !!(store.state.studyDetails?.study_metadata?.id));

    const matrixScrollEl = ref(null);

    // Preview indices + modal control
    const showPreviewModal = ref(false);
    const previewVisitIndex = ref(0);
    const previewGroupIndex = ref(0);

    // Dialog state
    const showInfo = ref(false);
    const showDialog = ref(false);
    const dialogMessage = ref("");
    const showLogicalIssuesDialog = ref(false);
    const logicalIssuesGrouped = ref([]);

    // Success dialog state (with CTA)
    const showSaveSuccessDialog = ref(false);
    const saveSuccessMessage = ref("");

    // Empty-visit modal state
    const showEmptyVisitsModal = ref(false);
    const emptyVisitIndices = ref([]);
    const isSavingInProgress = ref(false);
    const isPublishingStudy = ref(false);
    const lastSavedAt = ref(null);
    const saveError = ref("");

    // Confirm changes
    const showConfirmChanges = ref(false);
    const snapshotFrom = ref(null);
    const snapshotTo = ref(null);

    // Baseline (server)
    const baseline = ref(null);

    // Has any data already been saved for this study?
    const hasAnyData = ref(false);

    // ---------- UNSAVED GUARD STATE ----------
    const showUnsavedDialog = ref(false);
    const unsavedBusy = ref(false);
    const allowInternalNav = ref(false);

    // Prevent duplicate unsaved dialogs (route guard + parent guard/logo nav race)
    const unsavedDialogOpenOrPending = ref(false);

    // Local dirty tracking
    const isDirty = ref(false);
    const lastSavedSnapshot = ref(null);

    const globalDirty = computed(() => !!store.state.studyCreationDirty);
    const studyStatus = computed(() => {
      const storedStatus = String(
        store.state.studyDetails?.study_metadata?.status || ""
      ).trim().toUpperCase();
      return storedStatus || (isEditing.value ? "UNKNOWN" : "DRAFT");
    });
    const saveDirty = computed(() => !!isDirty.value || !!globalDirty.value);
    const unsavedSaveLabel = computed(() =>
      studyStatus.value === "PUBLISHED" ? "Review and save changes" : "Save as Draft & Exit"
    );
    const dialogListItems = computed(() => {
      const text = String(dialogMessage.value || "").trim();
      if (!text) return [];

      const lines = text
        .split("\n")
        .map(line => line.trim())
        .filter(Boolean);

      const numberedItems = lines
        .filter(line => /^\d+\.\s+/.test(line))
        .map(line => line.replace(/^\d+\.\s+/, "").trim());

      return numberedItems;
    });
    // Display lists
    const visitList = computed(() =>
      props.visits.length ? props.visits : [{ name: "All Visits" }]
    );
    const groupList = computed(() =>
      props.groups.length ? props.groups : [{ name: "All Groups" }]
    );
    const totalModels = computed(() => props.selectedModels.length);
    const totalVisits = computed(() => props.visits.length);
    const totalGroups = computed(() => props.groups.length);

    const hasAnyAssignment = computed(() => {
      return props.visits.some((_, vIdx) =>
        props.groups.some((_, gIdx) =>
          props.selectedModels.some((_, mIdx) =>
            props.assignments?.[mIdx]?.[vIdx]?.[gIdx] === true
          )
        )
      );
    });

    /* ============================================================
       FIELD / LOGIC HELPERS
       ============================================================ */
    function getFieldStableKey(field, mIdx, fIdx) {
      return String(
        field?._id ||
        field?.id ||
        field?.field_id ||
        field?.uid ||
        field?.name ||
        `m${mIdx}_f${fIdx}`
      );
    }

    function buildFieldRegistry() {
      const registry = new Map();

      (props.selectedModels || []).forEach((model, mIdx) => {
        (model?.fields || []).forEach((field, fIdx) => {
          const key = getFieldStableKey(field, mIdx, fIdx);
          registry.set(key, {
            key,
            mIdx,
            fIdx,
            modelTitle: model?.title || `Section ${mIdx + 1}`,
            fieldLabel: field?.label || field?.name || `Field ${fIdx + 1}`,
            fieldType: field?.type || "text",
            field
          });
        });
      });

      return registry;
    }

    function relationKeyForVisitGroup(vIdx, gIdx, relationType, participantKeys) {
      const joined = [...participantKeys].sort().join("|");
      return `${relationType}|v${vIdx}|g${gIdx}|${joined}`;
    }

    function visitGroupLabel(vIdx, gIdx) {
      const visitName = props.visits?.[vIdx]?.name || `Visit ${vIdx + 1}`;
      const groupName = props.groups?.[gIdx]?.name || `Group ${gIdx + 1}`;
      return `Visit "${visitName}" / Group "${groupName}"`;
    }

    function fieldRefLabel(meta) {
      if (!meta) return "Unknown field";
      return `${meta.modelTitle}.${meta.fieldLabel}`;
    }

    function assignedStateForModels(modelIndices, vIdx, gIdx) {
      return modelIndices.map((mIdx) => ({
        mIdx,
        assigned: !!props.assignments?.[mIdx]?.[vIdx]?.[gIdx]
      }));
    }

    function collectVisibilityConsistencyErrors(registry, seen) {
      const errors = [];

      (props.selectedModels || []).forEach((model, targetMIdx) => {
        (model?.fields || []).forEach((field, fIdx) => {
          const visibilityLogic = field?.constraints?.visibilityLogic;
          const rules = Array.isArray(visibilityLogic?.rules) ? visibilityLogic.rules : [];
          if (!rules.length) return;

          const targetMeta = registry.get(getFieldStableKey(field, targetMIdx, fIdx));

          rules.forEach((rule, rIdx) => {
            const sourceKey = String(rule?.sourceFieldKey || "");
            if (!sourceKey) return;

            const sourceMeta = registry.get(sourceKey);
            if (!sourceMeta) {
              errors.push(
                `Visibility rule error in ${fieldRefLabel(targetMeta)}: the source field used in its visibility logic could not be found. It may have been deleted or renamed.`
              );
              return;
            }

            for (let vIdx = 0; vIdx < totalVisits.value; vIdx++) {
              for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
                const states = assignedStateForModels([targetMIdx, sourceMeta.mIdx], vIdx, gIdx);
                const assignedCount = states.filter(x => x.assigned).length;

                if (assignedCount > 0 && assignedCount < states.length) {
                  const dedupeKey = relationKeyForVisitGroup(
                    vIdx,
                    gIdx,
                    `visibility:${targetMeta.key}:${sourceMeta.key}:${rIdx}`,
                    [targetMeta.key, sourceMeta.key]
                  );
                  if (seen.has(dedupeKey)) continue;
                  seen.add(dedupeKey);

                  errors.push(
                    `${visitGroupLabel(vIdx, gIdx)}: ${fieldRefLabel(targetMeta)} has visibility logic depending on ${fieldRefLabel(sourceMeta)}, but both sections are not assigned together.`
                  );
                }
              }
            }
          });
        });
      });

      return errors;
    }

    function collectCalculationConsistencyErrors(registry, seen) {
      const errors = [];
      const form = Array.isArray(props.forms) && props.forms.length ? props.forms[0] : null;
      const calculations = Array.isArray(form?.logic?.calculations) ? form.logic.calculations : [];

      calculations.forEach((rule, ruleIdx) => {
        const targetKey = String(rule?.target || "");
        const sourceKeys = Array.isArray(rule?.sources) ? rule.sources.map(String).filter(Boolean) : [];

        if (!targetKey || !sourceKeys.length) return;

        const targetMeta = registry.get(targetKey);
        if (!targetMeta) {
          errors.push(`Calculation rule error: target field with id "${targetKey}" was not found.`);
          return;
        }

        const sourceMetas = [];
        let missingSource = false;

        sourceKeys.forEach((srcKey) => {
          const meta = registry.get(srcKey);
          if (!meta) {
            errors.push(
              `Calculation rule error for ${fieldRefLabel(targetMeta)}: source field with id "${srcKey}" was not found.`
            );
            missingSource = true;
            return;
          }
          sourceMetas.push(meta);
        });

        if (missingSource) return;

        const participantMetas = [targetMeta, ...sourceMetas];
        const participantModelIndices = Array.from(new Set(participantMetas.map(x => x.mIdx)));
        const participantKeys = participantMetas.map(x => x.key);

        for (let vIdx = 0; vIdx < totalVisits.value; vIdx++) {
          for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
            const states = assignedStateForModels(participantModelIndices, vIdx, gIdx);
            const assignedCount = states.filter(x => x.assigned).length;

            if (assignedCount > 0 && assignedCount < states.length) {
              const dedupeKey = relationKeyForVisitGroup(
                vIdx,
                gIdx,
                `calculation:${ruleIdx}:${targetMeta.key}`,
                participantKeys
              );
              if (seen.has(dedupeKey)) continue;
              seen.add(dedupeKey);

              const sourceLabelText = sourceMetas.map(fieldRefLabel).join(", ");
              errors.push(
                `${visitGroupLabel(vIdx, gIdx)}: calculation target ${fieldRefLabel(targetMeta)} depends on ${sourceLabelText}, but all related sections are not assigned together.`
              );
            }
          }
        }
      });

      return errors;
    }

    function validateInterSectionLogicConsistency() {
      const errors = [];
      const seen = new Set();
      const registry = buildFieldRegistry();

      errors.push(...collectVisibilityConsistencyErrors(registry, seen));
      errors.push(...collectCalculationConsistencyErrors(registry, seen));

      return errors;
    }

    // ---------- DIRTY SNAPSHOT HELPERS ----------
    function buildDirtySnapshot() {
      const sd = store.state.studyDetails || {};
      const meta = sd.study_metadata || {};
      return deepClone({
        // Matrix props
        visits: props.visits || [],
        groups: props.groups || [],
        selectedModels: props.selectedModels || [],
        assignments: props.assignments || [],

        // Upstream study data that affects final payload and can change before matrix
        study: sd.study || {},
        subjects: sd.subjects || [],
        subjectCount: sd.subjectCount ?? 0,
        assignmentMethod: sd.assignmentMethod ?? "random",
        skipSubjectCreationNow: !!sd.skipSubjectCreationNow,

        // Metadata fields relevant to publish/draft transitions
        studyId: meta.id ?? null,
        status: meta.status ?? null,
        study_name: meta.study_name ?? meta.name ?? "",
        study_description: meta.study_description ?? meta.description ?? "",
      });
    }

    function markSavedSnapshot(recordSaveTime = false) {
      lastSavedSnapshot.value = buildDirtySnapshot();
      isDirty.value = false;
      if (store?._mutations?.setStudyCreationDirty) {
        store.commit("setStudyCreationDirty", false);
      }
      if (recordSaveTime) lastSavedAt.value = Date.now();
      saveError.value = "";
    }

    function buildSaveFingerprint() {
      const snapshot = buildDirtySnapshot();
      delete snapshot.studyId;
      delete snapshot.status;
      return JSON.stringify(snapshot);
    }

    function computeDirty() {
      try {
        const a = JSON.stringify(buildDirtySnapshot());
        const b = JSON.stringify(lastSavedSnapshot.value || {});
        isDirty.value = a !== b;
      } catch {
        isDirty.value = true;
      }

      // only set global dirty to true here; clearing happens only on successful save
      if (isDirty.value && store?._mutations?.setStudyCreationDirty) {
        store.commit("setStudyCreationDirty", true);
      }
    }

    function hasUnsavedChanges() {
      computeDirty();
      return isDirty.value || globalDirty.value;
    }

    function openUnsavedDialog() {
      if (unsavedDialogOpenOrPending.value || showUnsavedDialog.value) return;
      unsavedDialogOpenOrPending.value = true;
      showUnsavedDialog.value = true;
    }

    function closeUnsavedDialog() {
      showUnsavedDialog.value = false;
      unsavedBusy.value = false;
      unsavedDialogOpenOrPending.value = false;
    }

    // ---------- MATRIX TOGGLES ----------
    function onToggle(mIdx, vIdx, gIdx, checked) {
      emit("assignment-updated", { mIdx, vIdx, gIdx, checked });
    }

    // --------- BULK HELPERS ---------
    function isModelFullySelected(mIdx) {
      if (!totalVisits.value || !totalGroups.value) return false;
      for (let vIdx = 0; vIdx < totalVisits.value; vIdx++) {
        for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
          if (!props.assignments?.[mIdx]?.[vIdx]?.[gIdx]) return false;
        }
      }
      return true;
    }

    function isVisitFullySelected(vIdx) {
      if (!totalModels.value || !totalGroups.value) return false;
      for (let mIdx = 0; mIdx < totalModels.value; mIdx++) {
        for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
          if (!props.assignments?.[mIdx]?.[vIdx]?.[gIdx]) return false;
        }
      }
      return true;
    }

    const isEverythingSelected = computed(() => {
      if (!totalModels.value || !totalVisits.value || !totalGroups.value) return false;
      for (let mIdx = 0; mIdx < totalModels.value; mIdx++) {
        if (!isModelFullySelected(mIdx)) return false;
      }
      return true;
    });

    function onToggleModelAll(mIdx, checked) {
      if (!totalVisits.value || !totalGroups.value) return;
      for (let vIdx = 0; vIdx < totalVisits.value; vIdx++) {
        for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
          const current = !!props.assignments?.[mIdx]?.[vIdx]?.[gIdx];
          if (current !== checked) {
            emit("assignment-updated", { mIdx, vIdx, gIdx, checked });
          }
        }
      }
    }

    function onToggleVisitAll(vIdx, checked) {
      if (!totalModels.value || !totalGroups.value) return;
      for (let mIdx = 0; mIdx < totalModels.value; mIdx++) {
        for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
          const current = !!props.assignments?.[mIdx]?.[vIdx]?.[gIdx];
          if (current !== checked) {
            emit("assignment-updated", { mIdx, vIdx, gIdx, checked });
          }
        }
      }
    }

    function onToggleAll(checked) {
      if (!totalModels.value || !totalVisits.value || !totalGroups.value) return;
      for (let mIdx = 0; mIdx < totalModels.value; mIdx++) {
        for (let vIdx = 0; vIdx < totalVisits.value; vIdx++) {
          for (let gIdx = 0; gIdx < totalGroups.value; gIdx++) {
            const current = !!props.assignments?.[mIdx]?.[vIdx]?.[gIdx];
            if (current !== checked) {
              emit("assignment-updated", { mIdx, vIdx, gIdx, checked });
            }
          }
        }
      }
    }

    // Preview helpers
    function groupsForVisit(vIdx) {
      return groupList.value
        .map((_, idx) => idx)
        .filter(gIdx =>
          props.selectedModels.some((_, mIdx) =>
            props.assignments[mIdx][vIdx]?.[gIdx]
          )
        );
    }
    const assignedGroups = computed(() => groupsForVisit(previewVisitIndex.value));
    const previewGroupPos = computed(() => assignedGroups.value.indexOf(previewGroupIndex.value));
    function setFirstGroup(vIdx) {
      const arr = groupsForVisit(vIdx);
      previewGroupIndex.value = arr.length ? arr[0] : 0;
    }

    // Preview nav
    function prevPreviewVisit() {
      if (previewVisitIndex.value > 0) { previewVisitIndex.value--; setFirstGroup(previewVisitIndex.value); }
    }
    function nextPreviewVisit() {
      if (previewVisitIndex.value < visitList.value.length - 1) { previewVisitIndex.value++; setFirstGroup(previewVisitIndex.value); }
    }
    function prevPreviewGroup() {
      if (previewGroupPos.value > 0) { previewGroupIndex.value = assignedGroups.value[previewGroupPos.value - 1]; }
    }
    function nextPreviewGroup() {
      if (previewGroupPos.value < assignedGroups.value.length - 1) { previewGroupIndex.value = assignedGroups.value[previewGroupPos.value + 1]; }
    }

    // Build preview form
    const previewForm = computed(() => {
      const visitName = visitList.value[previewVisitIndex.value].name;
      const groupName = groupList.value[previewGroupIndex.value].name;
      const sections = props.selectedModels
        .filter((m, mIdx) =>
          m.fields && m.fields.length &&
          props.assignments[mIdx][previewVisitIndex.value]?.[previewGroupIndex.value]
        )
        .map(m => ({ title: m.title, fields: m.fields }));
      return { formName: `Preview: ${visitName} / ${groupName}`, sections };
    });

    // At least one assignment in a visit?
    function hasAssignment(vIdx) {
      return groupList.value.some((_, gIdx) =>
        props.selectedModels.some((_, mIdx) =>
          props.assignments[mIdx]?.[vIdx]?.[gIdx] === true
        )
      );
    }

    // STRICT check against real visits (for Save)
    function hasAssignmentInVisit(vIdx) {
      return props.groups.some((_, gIdx) =>
        props.selectedModels.some((_, mIdx) =>
          props.assignments?.[mIdx]?.[vIdx]?.[gIdx] === true
        )
      );
    }
    function computeEmptyVisits() {
      const empties = [];
      const realVisitsCount = props.visits.length;
      for (let v = 0; v < realVisitsCount; v++) if (!hasAssignmentInVisit(v)) empties.push(v);
      return empties;
    }

    // ---------- SNAPSHOT BUILDERS ----------
    function buildSnapshotFromBaseline(b) {
      const base = b || {};
      return {
        study: base.study || {},
        groups: base.groups || [],
        visits: base.visits || [],
        subjects: base.subjects || [],
        selectedModels: base.selectedModels || [],
        assignments: base.assignments || []
      };
    }
    function buildSnapshotFromCurrent() {
      const sd = store.state.studyDetails || {};
      return {
        study: sd.study || {},
        groups: deepClone(props.groups || []),
        visits: deepClone(props.visits || []),
        subjects: sd.subjects || [],
        selectedModels: deepClone(props.selectedModels || []),
        assignments: deepClone(props.assignments || [])
      };
    }

    // ---------- BASELINE & DATA PRESENCE ----------
    async function loadBaselineFromServer() {
      const studyId = store.state.studyDetails?.study_metadata?.id;
      if (!studyId) {
        baseline.value = buildSnapshotFromCurrent();
        return;
      }
      try {
        const resp = await axios.get(`/forms/studies/${studyId}`, { headers: { Authorization: `Bearer ${store.state.token}` } });
        const payload = resp.data || {};
        const metadata = payload.metadata || payload.study_metadata || {};
        const studyData = payload.content?.study_data || {};

        baseline.value = {
          study: {
            id: metadata.id,
            title: (studyData.study?.title ?? metadata.study_name) || "",
            description: (studyData.study?.description ?? metadata.study_description) || ""
          },
          groups: studyData.groups || [],
          visits: studyData.visits || [],
          subjects: studyData.subjects || [],
          selectedModels: studyData.selectedModels || [],
          assignments: studyData.assignments || []
        };
      } catch (e) {
        // fall back to current snapshot
        baseline.value = buildSnapshotFromCurrent();
      }
    }

    async function loadHasAnyData() {
      const studyId = store.state.studyDetails?.study_metadata?.id;
      if (!studyId) {
        hasAnyData.value = false;
        return;
      }

      try {
        const resp = await axios.get(`/forms/studies/${studyId}/data_entries`, {
          headers: { Authorization: `Bearer ${store.state.token}` },
        });
        const list = Array.isArray(resp.data) ? resp.data : resp.data?.entries || [];
        hasAnyData.value = list.length > 0;
      } catch (e) {
        // If check fails, avoid false structural prompts
        hasAnyData.value = false;
      }
    }

    // ---------- SAVE ----------
    // mode:
    // - "publish" => actual Save button
    // - "draft"   => unsaved dialog Save as Draft & Exit
    async function saveStudy(mode = "publish") {
      const isDraftMode = mode === "draft";

      const logicErrors = validateInterSectionLogicConsistency();
      if (logicErrors.length) {
        const maxShown = 12;
        const lines = logicErrors.slice(0, maxShown).map((msg, idx) => `${idx + 1}. ${msg}`);
        const more =
          logicErrors.length > maxShown
            ? `\n\n…and ${logicErrors.length - maxShown} more inconsistency(s).`
            : "";

        showDialogMessage(
          `Logical inconsistencies found in the Protocol Matrix:\n\n${lines.join("\n")}${more}\n\nPlease fix these assignments before saving.`
        );
        return;
      }

      // Publish path only: empty-visit warning
      if (!isDraftMode) {
        const empties = computeEmptyVisits();
        if (empties.length > 0) {
          emptyVisitIndices.value = empties;
          showEmptyVisitsModal.value = true;
          return;
        }
      }

      // Publish path only: structural confirm for editing with existing data
      if (!isDraftMode && isEditing.value) {
        const fromSnap = buildSnapshotFromBaseline(baseline.value);
        const toSnap = buildSnapshotFromCurrent();
        const diff = computeTemplateDiff(fromSnap, toSnap);

        const shouldPrompt = hasAnyData.value && diff.anyStructural;
        if (shouldPrompt) {
          snapshotFrom.value = fromSnap;
          snapshotTo.value = toSnap;
          showConfirmChanges.value = true;
          return;
        }
      }

      return await saveStudyImpl({ mode });
    }

    function showDialogMessage(message) {
      const parsed = parseLogicalIssuesMessage(message);

      if (parsed.length) {
        logicalIssuesGrouped.value = parsed;
        showLogicalIssuesDialog.value = true;
        showDialog.value = false;
        dialogMessage.value = "";
        return;
      }

      dialogMessage.value = message;
      showDialog.value = true;
    }

    function closeDialog() {
      showDialog.value = false;
      dialogMessage.value = "";
    }
    function closeLogicalIssuesDialog() {
      showLogicalIssuesDialog.value = false;
      logicalIssuesGrouped.value = [];
    }

    function parseFieldPath(path) {
      const raw = String(path || "").trim();
      if (!raw) {
        return {
          section: "Section",
          field: "Field"
        };
      }

      const firstDot = raw.indexOf(".");
      if (firstDot === -1) {
        return {
          section: raw,
          field: raw
        };
      }

      return {
        section: raw.slice(0, firstDot).trim() || "Section",
        field: raw.slice(firstDot + 1).trim() || "Field"
      };
    }

    function parseLogicalIssuesMessage(message) {
      const text = String(message || "").trim();
      if (!text.startsWith("Logical inconsistencies found")) return [];

      const matches = [...text.matchAll(
        /Visit\s+"([^"]+)"\s*\/\s*Group\s+"([^"]+)":\s*(.+?)\s+has visibility logic depending on\s+(.+?),\s+but both sections are not assigned together\./g
      )];

      if (!matches.length) return [];

      const groupedMap = new Map();

      matches.forEach((m) => {
        const visitName = m[1];
        const groupName = m[2];
        const target = parseFieldPath(m[3]);
        const source = parseFieldPath(m[4]);

        const key = `${visitName}__${groupName}`;
        if (!groupedMap.has(key)) {
          groupedMap.set(key, {
            visitName,
            groupName,
            issues: []
          });
        }

        groupedMap.get(key).issues.push({
          targetSection: target.section,
          targetField: target.field,
          sourceSection: source.section,
          sourceField: source.field
        });
      });

      return Array.from(groupedMap.values());
    }

    function openSaveSuccessDialog(message) {
      saveSuccessMessage.value = message;
      showSaveSuccessDialog.value = true;
    }

    function closeSaveSuccessDialog() {
      showSaveSuccessDialog.value = false;
      saveSuccessMessage.value = "";
    }

    function onSuccessViewSavedStudy() {
      closeSaveSuccessDialog();
      goToSaved();
    }

    function cancelConfirm() {
      showConfirmChanges.value = false;
    }

    async function confirmAndSave() {
      const logicErrors = validateInterSectionLogicConsistency();
      if (logicErrors.length) {
        const maxShown = 12;
        const lines = logicErrors.slice(0, maxShown).map((msg, idx) => `${idx + 1}. ${msg}`);
        const more =
          logicErrors.length > maxShown
            ? `\n\n…and ${logicErrors.length - maxShown} more inconsistency(s).`
            : "";

        showConfirmChanges.value = false;
        showDialogMessage(
          `Logical inconsistencies found in the Protocol Matrix:\n\n${lines.join("\n")}${more}\n\nPlease fix these assignments before saving.`
        );
        return;
      }

      showConfirmChanges.value = false;
      await saveStudyImpl({ mode: "publish" });
    }

    function closeEmptyVisitsModal() {
      showEmptyVisitsModal.value = false;
    }

    function goToFirstEmptyVisit() {
      const firstEmpty = emptyVisitIndices.value.length ? emptyVisitIndices.value[0] : 0;
      showEmptyVisitsModal.value = false;

      nextTick(() => {
        const el = matrixScrollEl.value;
        if (!el) return;

        const firstVisitHeader = el.querySelector(".visit-header");
        const visitWidth = firstVisitHeader ? firstVisitHeader.offsetWidth : 180;
        const targetLeft = Math.max(0, firstEmpty * visitWidth);

        el.scrollTo({
          left: targetLeft,
          top: 0,
          behavior: "smooth",
        });
      });
    }

    async function saveAnyway() {
      isSavingInProgress.value = true;
      try {
        const logicErrors = validateInterSectionLogicConsistency();
        if (logicErrors.length) {
          const maxShown = 12;
          const lines = logicErrors.slice(0, maxShown).map((msg, idx) => `${idx + 1}. ${msg}`);
          const more =
            logicErrors.length > maxShown
              ? `\n\n…and ${logicErrors.length - maxShown} more inconsistency(s).`
              : "";

          showDialogMessage(
            `Logical inconsistencies found in the Protocol Matrix:\n\n${lines.join("\n")}${more}\n\nPlease fix these assignments before saving.`
          );
          return;
        }

        await saveStudyImpl({ mode: "publish" });
      } finally {
        isSavingInProgress.value = false;
        showEmptyVisitsModal.value = false;
      }
    }

    // --- Save impl ---
    // mode = "publish" | "draft"
    async function saveStudyImpl({ mode = "publish", automatic = false } = {}) {
      const studyDetails = store.state.studyDetails || {};
      const userId = store.state.user?.id;
      const studyId = studyDetails.study_metadata?.id;

      if (!userId) {
        showDialogMessage("Please log in again.");
        return false;
      }

      const existingMeta = studyDetails.study_metadata || {};
      const targetStatus = mode === "draft" ? "DRAFT" : "PUBLISHED";

      // IMPORTANT: explicit status is sent so backend PUT can transition DRAFT -> PUBLISHED
      const metadata = studyId
        ? {
            created_by: existingMeta.created_by ?? userId,
            study_name:
              studyDetails.study?.title ??
              existingMeta.study_name ??
              existingMeta.name ??
              "",
            study_description:
              studyDetails.study?.description ??
              existingMeta.study_description ??
              existingMeta.description ??
              "",
            status: targetStatus,
            last_completed_step: 6,
          }
        : {
            created_by: userId,
            study_name: studyDetails.study?.title ?? "",
            study_description: studyDetails.study?.description ?? "",
            status: targetStatus,
            last_completed_step: 6,
          };
      const normalizedForms = JSON.parse(JSON.stringify(props.forms || [])).map(form => ({
        sections: Array.isArray(form.sections)
          ? form.sections.map(sec => ({
              ...sec,
              fields: Array.isArray(sec.fields)
                ? sec.fields.map(field => ({
                    ...field,
                    constraints: field.constraints || {}
                  }))
                : []
            }))
          : [],
        logic: {
          version: form.logic?.version || 1,
          calculations: Array.isArray(form.logic?.calculations) ? form.logic.calculations : [],
          conditions: Array.isArray(form.logic?.conditions) ? form.logic.conditions : [],
          valueAssignments: Array.isArray(form.logic?.valueAssignments) ? form.logic.valueAssignments : []
        }
      }));

      const studyData = {
        study: {
          ...(studyDetails.study || {}),
          title: studyDetails.study?.title ?? metadata.study_name ?? "",
          description: studyDetails.study?.description ?? metadata.study_description ?? "",
        },
        groups: props.groups,
        visits: props.visits,
        subjects: Array.isArray(studyDetails.subjects) ? studyDetails.subjects : [],
        selectedModels: JSON.parse(JSON.stringify(props.selectedModels || [])).map(sec => ({
          ...sec,
          fields: Array.isArray(sec.fields)
            ? sec.fields.map(field => ({
                ...field,
                constraints: field.constraints || {}
              }))
            : []
        })),
        forms: normalizedForms,
        assignments: props.assignments,
        subjectCount: studyDetails.subjectCount ?? 0,
        assignmentMethod: studyDetails.assignmentMethod ?? "random",
        subjectIdConfig: studyDetails.subjectIdConfig || null,
        skipSubjectCreationNow: !!studyDetails.skipSubjectCreationNow,
      };

      const payload = { study_metadata: metadata, study_content: { study_data: studyData } };
      const saveFingerprint = buildSaveFingerprint();

      // Keep your backend's existing "draft create via query param" behavior for POST,
      // but use explicit metadata.status for PUT/transition logic.
      let url;
      let method;

      if (studyId) {
        method = "put";
        // PUT uses explicit study_metadata.status; query params are optional/noise.
        url = `/forms/studies/${studyId}`;
      } else {
        method = "post";
        if (mode === "draft") {
          url = `/forms/studies/?status=DRAFT&last_completed_step=6`;
        } else {
          url = `/forms/studies/`;
        }
      }

      if (mode === "publish") {
        isPublishingStudy.value = true;
      }

      try {
        // audit_label rules:
        // - POST create draft/publish: study did not exist yet
        // - PUT update draft/publish: study already exists (incl. DRAFT -> PUBLISHED transition)
        // - Draft save from Unsaved dialog is distinct from Publish button

        const baseHeaders = { Authorization: `Bearer ${store.state.token}` };

        // Decide label by action + mode + create/update
        const isCreate = !studyId;
        const isDraftMode = mode === "draft";

        const auditLabel =
          isDraftMode && isCreate ? "Save & Exit - Create New Study Draft" :
          isDraftMode && !isCreate ? "Save Draft & Exit - Update Draft" :
          !isDraftMode && isCreate ? "Create New Study" :
          "Publish Study";

        const axiosConfig = {
          headers: baseHeaders,
          __skipActivityTracker: automatic,
          // audit_label is used by backend audit log as a human-readable action label
          params: { audit_label: automatic ? "Automatic Draft Recovery Save" : auditLabel }
        };

        const response = await axios[method](url, payload, axiosConfig);

        const updatedMetadata = response.data?.study_metadata || response.data?.metadata || metadata;
        const updatedStudyData = response.data?.content?.study_data || studyData;

        store.commit("setStudyDetails", {
          study_metadata: {
            id: studyId || updatedMetadata?.id,
            study_name: updatedMetadata.study_name,
            name: updatedMetadata.study_name,
            description: updatedMetadata.study_description,
            study_description: updatedMetadata.study_description,
            created_at: updatedMetadata.created_at,
            updated_at: updatedMetadata.updated_at,
            created_by: updatedMetadata.created_by,
            status: updatedMetadata.status ?? targetStatus,
            draft_of_study_id: updatedMetadata.draft_of_study_id,
            last_completed_step:
              updatedMetadata.last_completed_step ??
              metadata.last_completed_step ??
              existingMeta.last_completed_step,
          },
          study: { id: studyId || updatedMetadata?.id, ...(updatedStudyData.study || {}) },
          groups: updatedStudyData.groups || [],
          visits: updatedStudyData.visits || [],
          subjectCount: updatedStudyData.subjectCount ?? 0,
          assignmentMethod: updatedStudyData.assignmentMethod ?? "random",
          subjectIdConfig: updatedStudyData.subjectIdConfig || studyDetails.subjectIdConfig || null,
          subjects: updatedStudyData.subjects || [],
          assignments: updatedStudyData.assignments || [],
          selectedModels: updatedStudyData.selectedModels || [],
          forms: updatedStudyData.forms || normalizedForms || [],
          skipSubjectCreationNow: !!updatedStudyData.skipSubjectCreationNow,
        });

        // Refresh baseline/data and clear dirty markers only after successful save
        await Promise.all([loadBaselineFromServer(), loadHasAnyData()]);
        await nextTick();
        lastSavedAt.value = Date.now();
        if (buildSaveFingerprint() === saveFingerprint) {
          markSavedSnapshot(true);
        } else {
          isDirty.value = true;
          store.commit("setStudyCreationDirty", true);
        }

        // Publish save => success dialog with CTA
        if (mode === "publish") {
          openSaveSuccessDialog(
            studyId
              ? "Study successfully updated and published!"
              : "Study successfully saved and published!"
          );
        }

        return true;
      } catch (error) {
        console.error(`[ProtocolMatrix saveStudyImpl] Error ${studyId ? "updating" : "saving"} study:`, error);
        const detail =
          error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message;
        saveError.value = detail || `Failed to ${studyId ? "update" : "save"} study.`;
        if (!automatic) showDialogMessage(saveError.value);
        return false;
      } finally {
        if (mode === "publish") {
          isPublishingStudy.value = false;
        }
      }
    }

    async function saveAndContinue() {
      if (unsavedBusy.value || !saveDirty.value) return;
      unsavedBusy.value = true;
      saveError.value = "";
      try {
        await saveStudy(studyStatus.value === "PUBLISHED" ? "publish" : "draft");
      } finally {
        unsavedBusy.value = false;
      }
    }

    async function saveDraftBeforeIdle() {
      if (studyStatus.value !== "DRAFT" || unsavedBusy.value || !saveDirty.value) return;

      const logicErrors = validateInterSectionLogicConsistency();
      if (logicErrors.length) {
        saveError.value = "Automatic draft save was skipped because the template contains logical inconsistencies.";
        return;
      }

      unsavedBusy.value = true;
      saveError.value = "";
      try {
        await saveStudyImpl({ mode: "draft", automatic: true });
      } finally {
        unsavedBusy.value = false;
      }
    }

    function openPreview() {
      if (!hasAnyAssignment.value) return;

      const firstVisitIdx = props.visits.findIndex((_, vIdx) => hasAssignment(vIdx));

      previewVisitIndex.value = firstVisitIdx >= 0 ? firstVisitIdx : 0;
      setFirstGroup(previewVisitIndex.value);

      showPreviewModal.value = true;
    }

    function closePreview() {
      showPreviewModal.value = false;
    }

    function goToSaved() {
      allowInternalNav.value = true;
      router
        .push({ name: "Dashboard", query: { openStudies: "true" } })
        .finally(() => {
          allowInternalNav.value = false;
        });
    }

    // ---------- UNSAVED DIALOG ACTIONS ----------
    function onUnsavedKeepEditing() {
      closeUnsavedDialog();
    }

    function onUnsavedExitWithoutSaving() {
      closeUnsavedDialog();
      allowInternalNav.value = true;
      router
        .push({ name: "Dashboard", query: { openStudies: "true" } })
        .finally(() => {
          allowInternalNav.value = false;
        });
    }

    async function onUnsavedSaveAndExit() {
      if (studyStatus.value === "PUBLISHED") {
        closeUnsavedDialog();
        await saveAndContinue();
        return;
      }
      if (unsavedBusy.value) return;
      unsavedBusy.value = true;

      try {
        const logicErrors = validateInterSectionLogicConsistency();
        if (logicErrors.length) {
          const maxShown = 12;
          const lines = logicErrors.slice(0, maxShown).map((msg, idx) => `${idx + 1}. ${msg}`);
          const more =
            logicErrors.length > maxShown
              ? `\n\n…and ${logicErrors.length - maxShown} more inconsistency(s).`
              : "";

          showDialogMessage(
            `Logical inconsistencies found in the Protocol Matrix:\n\n${lines.join("\n")}${more}\n\nPlease fix these assignments before saving.`
          );
          return;
        }

        const ok = await saveStudyImpl({ mode: "draft" });
        if (!ok) return;

        closeUnsavedDialog();

        allowInternalNav.value = true;
        router
          .push({ name: "Dashboard", query: { openStudies: "true" } })
          .finally(() => {
            allowInternalNav.value = false;
          });
      } finally {
        unsavedBusy.value = false;
      }
    }

    // ---------- NAVIGATION / UNLOAD GUARDS ----------
    function beforeUnloadHandler(e) {
      if (!hasUnsavedChanges()) return;
      e.preventDefault();
      e.returnValue = "";
    }

    onBeforeRouteLeave((to, from, next) => {
      if (allowInternalNav.value) return next();

      // Do not stack dialogs if one is already open/pending
      if (showUnsavedDialog.value || unsavedDialogOpenOrPending.value) {
        return next(false);
      }

      if (!hasUnsavedChanges()) return next();

      openUnsavedDialog();
      next(false);
    });

    // ---------- DIRTY WATCHERS ----------
    // Watch matrix props + upstream study state so unsaved dialog also triggers
    // when user changed earlier steps / scratch form and reaches protocol matrix.
    watch(
      () => [
        props.visits,
        props.groups,
        props.selectedModels,
        props.assignments,
        store.state.studyDetails?.study,
        store.state.studyDetails?.subjects,
        store.state.studyDetails?.subjectCount,
        store.state.studyDetails?.assignmentMethod,
        store.state.studyDetails?.skipSubjectCreationNow,
        store.state.studyDetails?.study_metadata?.status,
        store.state.studyDetails?.study_metadata?.id,
      ],
      () => computeDirty(),
      { deep: true }
    );

    onMounted(async () => {
      window.addEventListener(STUDY_BEFORE_IDLE_EVENT, saveDraftBeforeIdle);
      await Promise.all([loadBaselineFromServer(), loadHasAnyData()]);

      // Baseline is current snapshot when entering screen, but keep global dirty alive.
      // If upstream steps/scratchform were dirty before entering matrix, route guard must still prompt.
      lastSavedSnapshot.value = buildDirtySnapshot();
      isDirty.value = false;

      window.addEventListener("beforeunload", beforeUnloadHandler);
    });

    onBeforeUnmount(() => {
      window.removeEventListener(STUDY_BEFORE_IDLE_EVENT, saveDraftBeforeIdle);
      window.removeEventListener("beforeunload", beforeUnloadHandler);
    });



    return {
      icons,

      matrixScrollEl,

      // state
      visitList,
      groupList,
      isEditing,

      // preview
      showPreviewModal,
      previewVisitIndex,
      previewGroupIndex,
      assignedGroups,
      previewGroupPos,
      prevPreviewVisit,
      nextPreviewVisit,
      prevPreviewGroup,
      nextPreviewGroup,
      openPreview,
      closePreview,
      previewForm,

      // toggles
      onToggle,
      hasAssignment,
      hasAnyAssignment,

      // bulk helpers
      isModelFullySelected,
      isVisitFullySelected,
      isEverythingSelected,
      onToggleModelAll,
      onToggleVisitAll,
      onToggleAll,

      // dialogs
      showInfo,
      showDialog,
      dialogMessage,
      dialogListItems,
      closeDialog,

      // success dialog
      showSaveSuccessDialog,
      saveSuccessMessage,
      closeSaveSuccessDialog,
      onSuccessViewSavedStudy,

      // unsaved dialog
      showUnsavedDialog,
      unsavedBusy,
      onUnsavedKeepEditing,
      onUnsavedExitWithoutSaving,
      onUnsavedSaveAndExit,

      // empty-visit modal
      showEmptyVisitsModal,
      emptyVisitIndices,
      isSavingInProgress,
      isPublishingStudy,
      lastSavedAt,
      saveError,
      studyStatus,
      saveDirty,
      unsavedSaveLabel,
      closeEmptyVisitsModal,
      goToFirstEmptyVisit,
      saveAnyway,

      // confirm changes
      showConfirmChanges,
      cancelConfirm,
      confirmAndSave,
      snapshotFrom,
      snapshotTo,

      // save + route
      saveStudy,
      saveAndContinue,
      saveDraftBeforeIdle,
      goToSaved,

      showLogicalIssuesDialog,
      logicalIssuesGrouped,
      closeLogicalIssuesDialog,

    };
  },
};
</script>
<style scoped lang="scss">
@import "@/assets/styles/_base.scss";

/* Subtle app-like surface and spacing */
.protocol-matrix-container {
  display: flex;
  flex-direction: column;
  padding: 0;
  background: linear-gradient(180deg, #f7f9fc 0%, #ffffff 100%);
  border-radius: 12px;
  width: 100%;
  min-height: 0;
  min-width: 0;
  box-sizing: border-box;
}

.publishing-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.42);
}

.publishing-panel {
  width: min(340px, 100%);
  padding: 24px;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.22);
  text-align: center;
}

.publishing-spinner {
  width: 38px;
  height: 38px;
  margin: 0 auto 14px;
  border: 4px solid #dbeafe;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: publishing-spin 0.85s linear infinite;
}

.publishing-title {
  margin-bottom: 6px;
  color: #111827;
  font-size: 17px;
  font-weight: 700;
}

.publishing-text {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.45;
}

@keyframes publishing-spin {
  to {
    transform: rotate(360deg);
  }
}

.matrix-scroll {
  position: relative;
  width: 100%;
  max-width: none;
  min-width: 0;

  /* key fix: grow naturally, scroll only when content is taller than viewport */
  height: auto;
  max-height: calc(100vh - 40px);
  min-height: 0;

  overflow: auto;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid $border-color;

  --matrix-topbar-height: 64px;
}

/* Top bar must not move horizontally */
.matrix-topbar {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 300;

  width: 100%;
  min-width: 100%;
  box-sizing: border-box;

  display: grid;
  grid-template-columns: auto minmax(220px, 1fr) auto;
  align-items: center;
  gap: 10px;

  min-height: var(--matrix-topbar-height);
  padding: 8px 10px;

  background: #ffffff;
  border-bottom: 1px solid $border-color;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
}

.screen-title {
  margin: 0;
  font-weight: 800;
  font-size: clamp(17px, 1.8vw, 22px);
  color: #101828;
  text-align: center;
  line-height: 1.25;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.matrix-topbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.icon-btn {
  background: transparent;
  cursor: pointer;
  border: none;
  padding: 0;
}

.header-info-btn {
  font-size: 18px;
  color: #667085;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.header-info-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

/* Reusable surface */
.card-surface {
  border: 1px solid $border-color;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.05);
}

/* Table */
.protocol-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: #ffffff;
}

/* Sticky table header: this is the important fix */
.protocol-table thead {
  position: sticky;
  top: var(--matrix-topbar-height);
  z-index: 220;
}

.protocol-table thead th {
  background: #f8fafc;
  border-top: 1px solid $border-color;
  white-space: normal;
  vertical-align: middle;
  z-index: 220;
}

.protocol-table th,
.protocol-table td {
  border-right: 1px solid $border-color;
  border-bottom: 1px solid $border-color;
  padding: 10px 12px;
  text-align: center;
  font-size: 14px;
  vertical-align: middle;
  background: #ffffff;
}

.protocol-table th:first-child,
.protocol-table td:first-child {
  border-left: 1px solid $border-color;
}

/* Sticky first column */
.model-header-col,
.model-cell {
  position: sticky;
  left: 0;
  min-width: 270px;
  max-width: 360px;
  text-align: left;
}

.model-header-col {
  z-index: 260 !important;
  background: #eef4f9 !important;
  color: #101828;
  font-weight: 800;
  vertical-align: top;
}


.model-header-main {
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: space-between;
  gap: 14px;
}

.model-header-title {
  display: block;
  font-weight: 800;
  color: #101828;
  line-height: 1.25;
}

.model-global-toggle {
  align-self: flex-end;
  justify-content: flex-end;
  text-align: right;
  white-space: normal;
  max-width: 190px;
  margin-top: auto;
}

.model-global-toggle span {
  white-space: normal;
  line-height: 1.25;
}

.model-cell {
  z-index: 120;
  background: #ffffff !important;
}

.protocol-table tbody tr:nth-child(even) td {
  background: #fbfdff;
}

.protocol-table tbody tr:hover td {
  background: #eef6ff;
}

.protocol-table tbody tr:nth-child(even) .model-cell {
  background: #fbfdff !important;
}

.protocol-table tbody tr:hover .model-cell {
  background: #eef6ff !important;
}

.visit-header-col {
  min-width: 180px;
}

.group-header-col,
.assignment-cell {
  min-width: 140px;
}

.visit-header-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 38px;
}

.visit-header-main {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 220px;
}

.th-title {
  color: #667085;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.th-chip,
.visit-name {
  display: inline-block;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
  line-height: 1.25;
  max-width: clamp(18ch, 20vw, 36ch);
  text-align: center;
}

.th-chip {
  font-size: 12px;
  background: #eef2ff;
  color: #3538cd;
  border: 1px solid #e0e7ff;
  padding: 2px 8px;
  border-radius: 999px;
  font-weight: 700;
}

.group-name {
  font-weight: 700;
  color: #101828;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.25;
}

.model-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.model-title {
  text-align: left;
  font-weight: 700;
  color: #101828;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
}

/* Checkbox using Font Awesome */
.chk-wrap {
  --size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--size);
  height: var(--size);
  position: relative;
  cursor: pointer;
  background: transparent;
}

.chk-wrap input {
  opacity: 0;
  width: var(--size);
  height: var(--size);
  position: absolute;
  margin: 0;
  cursor: pointer;
}

.fa-chk {
  font-size: 19px;
  line-height: 1;
  pointer-events: none;
  color: #98a2b3;
}

.chk-wrap input:checked + .fa-chk {
  color: $primary-color;
}

.chk-wrap:focus-within .fa-chk {
  outline: 2px solid rgba(52, 96, 255, 0.25);
  outline-offset: 2px;
}

/* Buttons */
.btn-option,
.btn-primary {
  padding: $button-padding;
  border-radius: $button-border-radius;
  cursor: pointer;
  font-size: 14px;
  border: 1px solid transparent;
  transition:
    transform 0.05s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease;
}

.btn-option {
  background: #ffffff;
  border: 1px solid $border-color;
  color: #111827;
}

.btn-option:hover:not(:disabled) {
  background: #f8fafc;
  box-shadow: 0 6px 14px rgba(16, 24, 40, 0.08);
}

.btn-primary {
  background: $primary-color;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: darken($primary-color, 6%);
  box-shadow: 0 8px 18px rgba(52, 96, 255, 0.25);
}

.btn-option:disabled,
.btn-primary:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.protocol-back-btn {
  justify-self: start;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 13px;
  cursor: pointer;
  color: $text-color;
  font-size: 14px;
  line-height: 1;
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    transform 0.02s ease;
}

.btn-back:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-back:active {
  transform: scale(0.98);
}

/* Bulk checkbox */
.bulk-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #111827;
  cursor: pointer;
  white-space: nowrap;
}

.bulk-toggle.small {
  font-size: 11px;
}

.bulk-toggle input {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid $border-color;
  background: #ffffff;
  display: inline-block;
  position: relative;
  margin: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.bulk-toggle input:checked {
  background: $primary-color;
  border-color: $primary-color;
}

.bulk-toggle input:checked::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 5px;
  width: 4px;
  height: 8px;
  border: 2px solid #ffffff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg);
}

.bulk-toggle span {
  line-height: 1.2;
}

/* Preview nav */
.nav-btn {
  background: #ffffff;
  padding: 8px 12px;
  border: 1px solid $border-color;
  border-radius: $button-border-radius;
  cursor: pointer;
  font-size: 16px;
  transition:
    transform 0.05s ease,
    box-shadow 0.2s ease,
    background 0.2s ease;
}

.nav-btn:hover:not(:disabled) {
  background: #f8fafc;
  box-shadow: 0 4px 10px rgba(16, 24, 40, 0.08);
}

.nav-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.preview-label {
  font-size: 13px;
  color: #344054;
  font-weight: 700;
}

.spacer {
  flex: 1;
}

.preview-content {
  margin-top: 14px;
  max-height: 65vh;
  overflow-y: auto;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 12px;
}

/* Modals */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 12, 34, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 12px;
}

.protocol-preview-modal,
.validation-modal {
  background: white;
  border-radius: 12px;
  width: 96%;
  max-width: 900px;
  padding: 20px;
  box-shadow: 0 20px 50px rgba(16, 24, 40, 0.18);
}

.validation-title {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
  color: #101828;
  display: flex;
  align-items: center;
  gap: 8px;
}

.validation-text {
  margin: 0 0 10px;
  color: #475467;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

/* Unsaved dialog actions */
.unsaved-actions {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
}

/* Confirm changes scroll handling */
.confirm-scroll {
  max-width: 1100px;
}

.diff-scroll {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid $border-color;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
}

/* Sticky footer inside confirm dialog */
.sticky-actions {
  position: sticky;
  bottom: 0;
  background: #fff;
  padding-top: 12px;
  margin-top: 12px;
  border-top: 1px solid $border-color;
}

/* Empty-visit list */
.empty-visits-list {
  margin: 0 0 8px 18px;
  padding: 0;
  list-style: disc;
  color: #344054;
  max-height: 40vh;
  overflow-y: auto;
}

.empty-visits-list .visit-item-title {
  display: inline-block;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  line-height: 1.3;
}

/* Small utility */
.li-icon {
  margin-right: 6px;
}

.mr-6 {
  margin-right: 6px;
}

/* Logical issues modal */
.logical-issues-modal {
  max-width: 980px;
}

.logical-issues-scroll {
  max-height: 58vh;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 12px;
  padding-right: 4px;
}

.logic-group-card {
  border: 1px solid $border-color;
  border-radius: 12px;
  background: #f8fafc;
  padding: 14px;
}

.logic-group-header {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.logic-group-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef2ff;
  border: 1px solid #dbe4ff;
  color: #3538cd;
  font-size: 12px;
  font-weight: 700;
}

.logic-dependency-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.logic-dependency-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  padding: 12px;
}

.logic-arrow-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.logic-arrow-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.logic-arrow-text {
  font-size: 11px;
  color: #667085;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.logic-arrow-line {
  font-size: 18px;
  color: #475467;
  font-weight: 800;
  line-height: 1;
}

.logic-node {
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid $border-color;
  min-width: 0;
}

.logic-node-current {
  background: #eff6ff;
  border-color: #bfdbfe;
}

.logic-node-source {
  background: #f9fafb;
  border-color: #d0d5dd;
}

.logic-node-section {
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  word-break: break-word;
}

.logic-node-field {
  margin-top: 2px;
  font-size: 14px;
  font-weight: 600;
  color: #101828;
  word-break: break-word;
}

.logic-dependency-note {
  margin-top: 8px;
  font-size: 12px;
  color: #667085;
  line-height: 1.4;
}

/* Pretty error modal */
.pretty-error-modal {
  max-width: 900px;
}

.pretty-error-content {
  margin-top: 10px;
}

.pretty-error-list {
  margin: 0;
  padding-left: 20px;
  max-height: 52vh;
  overflow-y: auto;
}

.pretty-error-item {
  margin-bottom: 10px;
  padding: 10px 12px;
  border: 1px solid $border-color;
  border-radius: 10px;
  background: #f8fafc;
  color: #344054;
  line-height: 1.45;
}

/* Scrollbar */
.matrix-scroll::-webkit-scrollbar {
  height: 11px;
  width: 11px;
}

.matrix-scroll::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.matrix-scroll::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.matrix-scroll::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 999px;
}

/* Mobile */
@media (max-width: 760px) {
  .protocol-matrix-container {
    height: auto;
  }

  .matrix-scroll {
   max-height: calc(100vh - 30px);
    --matrix-topbar-height: 138px;
  }

  .matrix-topbar {
    grid-template-columns: 1fr auto;
    align-items: start;
  }

  .protocol-back-btn {
    grid-column: 1 / 2;
    justify-self: start;
  }

  .header-info-btn {
    grid-column: 2;
    grid-row: 1;
  }

  .screen-title {
    grid-column: 1 / -1;
    grid-row: 2;
    text-align: left;
  }

  .matrix-topbar-actions {
    grid-column: 1 / -1;
    grid-row: 3;
    display: grid;
    grid-template-columns: 1fr;
    width: 100%;
  }

  .model-header-col,
  .model-cell {
    min-width: 220px;
    max-width: 260px;
  }

  .visit-header-col {
    min-width: 160px;
  }

  .group-header-col,
  .assignment-cell {
    min-width: 130px;
  }

  .unsaved-actions {
    grid-template-columns: 1fr;
  }

  .logic-arrow-row {
    grid-template-columns: 1fr;
  }

  .logic-arrow-wrap {
    flex-direction: row;
    justify-content: flex-start;
  }
}
</style>
